import React, { useEffect, useState } from "react";
import { ReactComponent as CloseEyeIcon } from "../assets/closeEyeIcon.svg";
import { ReactComponent as OpenEyeIcon } from "../assets/view_eyeIcon.svg";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { checkValidateData } from "../../../utils/validate";
import { GOOGLE_CLIENT, LOGIN_URL, NODE_URL, REDIRECT_URL } from "../../../utils/constants";
import { getUserData, getUserId, getUserToken, isKeepLoggedIn } from "../../../utils/utilityFunctions/localServices";
import { addParticularCompany, addUser, addUserCompanyAccess, removeUser, updateUser } from "../../../utils/userSlice";
import { useDispatch } from "react-redux";
import { useLazyFetchCompanyPermissionsQuery } from "../../../utils/customer/customerTable";
import validateLoginForm from "../../../utils/validationLogin";
import { applyThemeColors } from "../../Header";

const GOOGLE_CLIENT_ID = GOOGLE_CLIENT;
const GOOGLE_REDIRECT_URL = REDIRECT_URL; // must match Google Console setting
// const GOOGLE_REDIRECT_URL = "http://localhost:3000";

function base64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256(buffer) {
  return await crypto.subtle.digest("SHA-256", buffer);
}

function randomString(len = 64) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const LoginForm = () => {
  const dispatch = useDispatch();

  const { state, search } = useLocation();

  const query = new URLSearchParams(search);
  const clearStorage = query.get("cs");

  const signupData = state?.signupData;
  const authType = state?.authType;

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  // const [isReload, setIsReload] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (clearStorage === "t") {
      localStorage.clear();
    } else {
      console.log("clearStorage useParam", clearStorage);
      if (isKeepLoggedIn()) {
        const data = getUserData();
        processUserData(data);
      }
    }
  }, [clearStorage]);

  //company permission api
  const [fetchCompanyPermissions, { data: companyPermissionsData, isSuccess: companyPermissionsSuccess }] =
    useLazyFetchCompanyPermissionsQuery();

  const updateAndNagivate = async () => {
    dispatch(addUserCompanyAccess(companyPermissionsData?.data));
    localStorage.setItem("company_preference", JSON.stringify(companyPermissionsData?.data?.preferences));
    localStorage.setItem("userPreferences", JSON.stringify(companyPermissionsData?.data?.user_preferences));
    // document.documentElement.style.setProperty(
    //   "--color-primary",
    //   companyPermissionsData?.data.user_preferences.theme_color
    // );

    applyThemeColors(companyPermissionsData?.data?.user_preferences?.theme_color);
    navigate("/customers");
  };

  const processUserData = (data) => {
    dispatch(
      addParticularCompany({
        company_id: data?.company?.id,
        company_name: data?.company?.company_name,
        company_logo: data?.company?.company_logo,
        menuName: "",
      })
    );

    if (data?.user_id !== getUserId()) localStorage.removeItem("saved_view");

    dispatch(addUser(data));
    localStorage.setItem("ownerInfo", JSON.stringify({ isOwner: Boolean(data?.super_admin) }));

    if (data?.company_set === 1) {
      dispatch(updateUser(data));
    }
    if (data?.workspaces && data?.workspaces.length > 1) {
      navigate("/organisations", { state: { workspaces: data?.workspaces } });
    } else {
      fetchCompanyPermissions({ token: getUserToken(), companyId: data?.company?.id });
    }
  };

  useEffect(() => {
    if (companyPermissionsData && companyPermissionsData?.data) updateAndNagivate();
  }, [companyPermissionsData, companyPermissionsSuccess]);

  useEffect(() => {
    const checkGoogleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (code) {
        setIsFetching(true);
        const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
        if (!codeVerifier) {
          console.error("Missing PKCE code_verifier");
          return;
        }

        try {
          const response = await fetch(`${NODE_URL}auth/callback`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, codeVerifier }),
          });

          const res = await response.json();

          if (!res.success) {
            if (res.message === "User doesn't exists" && res.signup_uuid) {
              navigate("/onboarding/companySetup", {
                state: { signupData: { signup_uuid: res.signup_uuid, fullName: res.full_name } },
              });
            } else setErrorMessage({ apiError: res.error || "Invalid credentials. Please try again." });
            return;
          }

          const data = res?.data;

          processUserData(data);
        } catch (err) {
          console.error("Google login error:", err);
        }
      }
    };

    checkGoogleCallback();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    const emailValue = formData?.email?.trim();
    const passwordValue = formData?.password?.trim();

    const message = validateLoginForm(emailValue, passwordValue);
    setErrorMessage(message);
    if (Object.keys(message).length !== 0) return;

    const payload = { email: emailValue, password: passwordValue };

    try {
      const response = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const res = await response.json();

      if (!res.success) {
        setErrorMessage({ apiError: res.error || "Invalid credentials. Please try again." });
        return;
      }

      const data = res?.data;
      if (formData?.rememberMe) localStorage.setItem("userInfo", JSON.stringify({ keepLoggedIn: true }));
      processUserData(data);
    } catch (error) {
      console.error("Login Error:", error);
      setErrorMessage("Something went wrong. Please try again later.");
    }
  };

  const handleGoogleLogin = async () => {
    const codeVerifier = randomString(64);
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);

    const encoder = new TextEncoder().encode(codeVerifier);
    const hashed = await sha256(encoder);
    const codeChallenge = base64url(hashed);

    const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    url.searchParams.set("client_id", GOOGLE_CLIENT_ID);
    url.searchParams.set("redirect_uri", GOOGLE_REDIRECT_URL); // match what's registered
    url.searchParams.set("response_type", "code");
    url.searchParams.set("scope", "openid email profile");
    url.searchParams.set("code_challenge", codeChallenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("prompt", "select_account");

    window.location.assign(url.toString());
  };

  if (isFetching)
    return (
      <main className="min-h-screen w-1/2 flex items-center justify-center bg-white px-4 font-Inter">
        <section className="w-full max-w-sm bg-transparent py-10">
          <p className="text-center text-2xl font-bold text-primary pb-6">
            Fetching Your Details,
            <br /> Please Wait...
          </p>
        </section>
      </main>
    );

  return (
    <main className="min-h-screen w-1/2 flex items-center justify-center bg-white px-4 font-Inter">
      <section className="w-full max-w-sm bg-transparent py-10">
        <h1 className="text-center text-3xl font-bold text-primary">Welcome back</h1>
        <p className="text-center text-3xl font-bold text-primary pb-6">Login to your account</p>

        <form className="flex flex-col gap-4 w-full" aria-label="Login form">
          {/* Email */}
          <div className="flex flex-col gap-1 text-[#313131cc]">
            <label htmlFor="email" className="text-base font-medium ">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData?.email}
              className="w-full px-3 py-2 text-base border border-[#DFE2E5] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
              placeholder="Enter your email"
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {errorMessage?.email && <p className="text-F16136 text-xs">{errorMessage.email}</p>}
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1 text-[#313131cc]">
            <label htmlFor="password" className="text-base font-medium ">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData?.password}
                // className="w-full px-3 py-2 text-base border border-[#DFE2E5] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 pr-10"
                className={`w-full px-4 rounded-md border ${
                  errorMessage === "Password mismatch."
                    ? "border-2 border-[#F16136] focus:outline-none focus:border-[#F16136] focus:ring-0 focus:ring-transparent"
                    : "border-[#DFE2E5]  focus:outline-none focus:ring-2 focus:ring-blue-200"
                }`}
                placeholder="Enter your password"
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-2 flex items-center text-gray-500"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <OpenEyeIcon /> : <CloseEyeIcon />}
              </button>
            </div>

            {errorMessage?.password && <p className="text-F16136 text-xs">{errorMessage.password}</p>}
          </div>

          {/* Remember + Forgot Password */}
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2  text-primary text-base">
              <input
                type="checkbox"
                className="text-primary border border-primary rounded outline-none focus:outline-none focus:ring-0 focus:ring-transparent"
                checked={formData?.rememberMe}
                onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              />
              Remember
            </label>
            <a href="/forget_password_one" className=" text-[#313131cc] underline text-base">
              Forget Password?
            </a>
          </div>
          <div className="flex items-center justify-between ">
            {errorMessage?.apiError && <p className="text-F16136 text-xs">{errorMessage.apiError}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-2 rounded-md bg-primary hover:bg-primary-80 text-white text-lg font-bold"
            onClick={handleLogin}
          >
            Login
          </button>
          <div className="border-t border-dashed border-[#DFE2E5] w-[70%] m-auto mt-2" />
        </form>

        {/* Footer Link */}
        <div className="pt-5 text-center text-base text-[#666] font-normal">
          Don’t have an account?{" "}
          <Link to="/onboarding/signup" className="font-medium text-primary ">
            Sign up now.
          </Link>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="flex justify-center items-center gap-2 mt-2 w-full rounded-md border border-zinc-200 py-2 hover:bg-gray-50 transition-colors"
        >
          {/* Google SVG */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="inline-block"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_365_3346)">
              <path
                d="M23.094 9.91355L13.3046 9.91309C12.8724 9.91309 12.522 10.2634 12.522 10.6957V13.823C12.522 14.2552 12.8724 14.6056 13.3046 14.6056H18.8174C18.2137 16.1722 17.087 17.4842 15.6496 18.3178L18.0002 22.387C21.7709 20.2062 24.0002 16.3799 24.0002 12.0965C24.0002 11.4866 23.9553 11.0506 23.8653 10.5597C23.797 10.1867 23.4732 9.91355 23.094 9.91355Z"
                fill="#167EE6"
              />
              <path
                d="M11.9998 19.3047C9.30193 19.3047 6.94675 17.8306 5.68182 15.6494L1.61279 17.9948C3.6835 21.5836 7.56259 24.0003 11.9998 24.0003C14.1765 24.0003 16.2304 23.4143 17.9998 22.3929V22.3873L15.6491 18.3181C14.5739 18.9417 13.3297 19.3047 11.9998 19.3047Z"
                fill="#12B347"
              />
              <path
                d="M18 22.3922V22.3866L15.6494 18.3174C14.5741 18.941 13.33 19.304 12 19.304V23.9996C14.1767 23.9996 16.2308 23.4135 18 22.3922Z"
                fill="#0F993E"
              />
              <path
                d="M4.69566 12.0003C4.69566 10.6705 5.05856 9.42637 5.68205 8.3512L1.61302 6.00586C0.586031 7.76962 0 9.81797 0 12.0003C0 14.1826 0.586031 16.2309 1.61302 17.9947L5.68205 15.6494C5.05856 14.5742 4.69566 13.3301 4.69566 12.0003Z"
                fill="#FFD500"
              />
              <path
                d="M11.9998 4.69566C13.759 4.69566 15.375 5.32078 16.6372 6.36061C16.9486 6.61711 17.4012 6.59859 17.6864 6.31336L19.9022 4.09758C20.2258 3.77395 20.2028 3.24422 19.8571 2.94431C17.7423 1.10967 14.9907 0 11.9998 0C7.56259 0 3.6835 2.41673 1.61279 6.00558L5.68182 8.35092C6.94675 6.16969 9.30193 4.69566 11.9998 4.69566Z"
                fill="#FF4B26"
              />
              <path
                d="M16.6374 6.36061C16.9488 6.61711 17.4015 6.59859 17.6866 6.31336L19.9024 4.09758C20.226 3.77395 20.2029 3.24422 19.8573 2.94431C17.7425 1.10963 14.991 0 12 0V4.69566C13.7592 4.69566 15.3752 5.32078 16.6374 6.36061Z"
                fill="#D93F21"
              />
            </g>
            <defs>
              <clipPath id="clip0_365_3346">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span className="text-lg font-medium text-[#666] sm:text-base">Continue with Google</span>
        </button>
      </section>
    </main>
  );
};

export default LoginForm;
